import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer"
import { useApiCall } from "@jane/lib/src/api";
import { config } from "../../config";
import React, {FC, useEffect, useMemo, useState} from "react";
import { Breadcrumbs } from "@jane/lib/src/components/content/Breadcrumbs";
import {faChevronDown, faSquareCheck} from "@fortawesome/free-solid-svg-icons";
import {LearnCategory, LearnDashboardStatistic, LearnModule} from "../../../../lib/src/api/repositories/learn-repository";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight, faDot,} from "@fortawesome/pro-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import { Input } from "@jane/lib/src/components/form/Input";
import moment from "moment";
import {faSquare} from "@fortawesome/free-regular-svg-icons";
import {
  LearnDashboardExplorerContextProvider, LearnDashboardExplorerTeam,
  useLearnDashboardExplorerContext
} from "../../../../lib/src/learn/context/LearnDashboardExplorerContext";
import {Team} from "@jane/lib/src/api/repositories/teams-repository";
import {getSubTeamsRecursively} from "./resolveSubTeams";
import {Toggle} from "../../../../lib/src/components/form/Toggle";

export const LearnDashboardExplorerPage: React.FC = () => {
  return <ContentContainer size={'full'}>
    <LearnDashboardExplorerContextProvider config={config}>
      <LearnDashboardContent />
    </LearnDashboardExplorerContextProvider>
  </ContentContainer>
}

const LearnDashboardContent: FC<{}> = (props) => {
  const {fromDate, setFromDate, toDate, setToDate, categories, modules} = useLearnDashboardExplorerContext()
  return <>
    <div className={'flex items-stretch bg-white border-b border-brand-200 -mx-6 -mt-8 h-20'}>
      <div className={'flex-1 px-8 flex flex-col justify-center'}>
        <div className={'mt-5 -mb-1'}>
          <Breadcrumbs currentPage={'Dashboards'} crumbs={[
            {label: 'Kennisbank', href: '/learn'}
          ]} />
        </div>
        <div className={'py-2 text-2xl font-bold'}>Kennisbank Dashboard</div>
      </div>

      <div className={'flex px-8 py-4 border-l border-brand-200 gap-x-6 items-center'}>
        <div className={'text-brand-900 font-bold mr-2'}>Periode:</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(30, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>30d</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(90, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>90d</div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Van:'} value={fromDate} onChange={setFromDate} />
        </div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Tot en met:'} value={toDate} onChange={setToDate} />
        </div>
      </div>
    </div>

    {/* Dashboard Tables
     */}
    <ProgramTable programs={categories} modules={modules} fromDate={fromDate} toDate={toDate} />
    <TeamProgressTable />
  </>
}


const ProgramTable: FC<{ programs: LearnCategory[], modules: LearnModule[], fromDate: Date, toDate: Date }> = props => {
  const pinnendCategories = props.programs.filter((cat) => cat.is_pinned_category)
  return <div className={'mx-2 mt-8'}>
    <div className={'text-2xl font-bold mb-4'}>Programma selecteren</div>
    <div className={"overflow-hidden rounded-xl border border-gray-200 bg-white"}>
      <table className={"w-full border-collapse"}>
        <thead>
        <tr className={"bg-gray-50 text-left"}>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Programma
          </th>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Modules toegewezen
          </th>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Modules gestart
          </th>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Modules afgerond
          </th>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Modules vervallen
          </th>
          <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
            Certificaten uitgedeeld
          </th>
          <th className={"px-4 py-3"}></th>
        </tr>
        </thead>
        <tbody>
        {pinnendCategories.map((pinCat, i) => (
          <ProgramTableRow key={i} category={pinCat} />
        ))}
        </tbody>
      </table>
    </div>
  </div>
}

const ProgramTableRow: FC<{ category: LearnCategory }> = (props) => {
  const {fromDate, toDate: upToDate, selectedCategories, setSelectedCategories, getModulesForCategories} = useLearnDashboardExplorerContext()
  const {learn} = useApiCall(config)
  const navigate = useNavigate()
  const [statistics, setStatistics] = useState<LearnDashboardStatistic|null>(null)

  const moduleIds = useMemo(() => {
    return getModulesForCategories([props.category.id])
  }, [getModulesForCategories, props.category])

  useEffect(() => {
    if (moduleIds.length > 0) {
      learn.fetchProgramDashboardStatistics(fromDate, upToDate, moduleIds).then((result) => {
        setStatistics(result)
      })
    }
  }, [moduleIds, fromDate, upToDate]);

  return <tr className={"border-t border-gray-200 h-14"}>
    <td className={"px-4 text-sm text-gray-500"}>
      <div className={'flex space-x-3 items-center'}>
        <FontAwesomeIcon icon={selectedCategories.find((c) => c === props.category.id) ? faSquareCheck : faSquare} className={`text-xl ${selectedCategories.find((c) => c === props.category.id) ? 'text-brand-primary' : 'text-gray-500'} py-1.5 px-1 rounded-sm hover:bg-red-50 hover:cursor-pointer`}
                         onClick={() => setSelectedCategories((prev) => prev.includes(props.category.id)
                           ? prev.filter((id) => id !== props.category.id)
                           : [...prev, props.category.id])}
        />
        <div className={'text-brand-primary font-semibold'}>{props.category.name}</div>
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.assigned_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.assigned_modules.this_period} previous={statistics.assigned_modules.previous_period} />
          : <div></div>}
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.started_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.started_modules.this_period} previous={statistics.started_modules.previous_period} />
          : <div></div>}
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.finished_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.finished_modules.this_period} previous={statistics.finished_modules.previous_period} />
          : <div></div>}
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.dropped_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.dropped_modules.this_period} previous={statistics.dropped_modules.previous_period} />
          : <div></div>}
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.awarded_certificates.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.awarded_certificates.this_period} previous={statistics.awarded_certificates.previous_period} />
          : <div></div>}
      </div>
    </td>

    <td className={"px-4 py-3"}>
      <div
        className={"flex flex-row items-center space-x-2 text-sm text-brand-primary transition hover:cursor-pointer hover:text-red-500"}
        onClick={() => {
          const containsSelectedRow = selectedCategories.includes(props.category.id)
          if (!containsSelectedRow) selectedCategories.push(props.category.id)
          navigate(`/dashboards-learn/programs?from=${moment(fromDate).format('DD-MM-yyyy')}&to=${moment(upToDate).format('DD-MM-yyyy')}${selectedCategories.length > 0 ? `&categories=${selectedCategories.join(',')}` : ''}`)
          }
        }
      >
        <div>Programma Dashboard</div>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
    </td>
  </tr>
}


const TeamProgressTable: FC<{}> = (props) => {
  const { explorerTeams, showModuleTable, setShowModuleTable } = useLearnDashboardExplorerContext()
  const parentTeams = useMemo(() => {
    return explorerTeams.filter((t) => t.parentObjectId === config.learnDashboardExplorerRootTeamId && t.hasMyTeamAsChild)
  }, [explorerTeams])

  return <>
    <div className={'mx-2 my-8'}>
      <div className={'text-2xl font-bold mb-4'}>Voortgang per team</div>
      <div>In de dataverkenner zijn verschillende statistieken betreffende de voortgang van kennisbank programma’s in te zien. Deze worden gefilterd op programma’s die je hierboven selecteerd.</div>
      <div>Start door rechts-boven een periode te selecteren, binnen deze periode worden de relevante cijfers getoond, ook wordt het verschil met de vorige periode van de zelfde duur getoond. Hier onder is per cluster, of per team de voortgang te zien.</div>
      <div>Op basis van de onderstaande toggle kan gekozen worden om de statistieken inzichtelijk te maken voor modules of medewerkers.</div>

      <div>Dit is opgedeeld in de volgende statistieken:</div>
      {showModuleTable ? <>
        <ul>
          <li>&middot; <span className={'font-bold'}>Mederwerkers Toegewezen:</span> Opsomming van het totaal aantal medewerks dat binnen deze periode aan een module toegewezen zijn.</li>
          <li>&middot; <span className={'font-bold'}>Mederwerkers Gestart:</span> Aantal medewerkers die binnen deze periode aan een module zijn begonnen.</li>
          <li>&middot; <span className={'font-bold'}>Mederwerkers Afgerond:</span> Aantal medewerkers die binnen deze periode een module hebben afgerond.</li>
          <li>&middot; <span className={'font-bold'}>Mederwerkers uitgetreden:</span> Opsomming van het aantal medewerkers dat binnen deze periode uit dienst is gegaan.</li>
          <li>&middot; <span className={'font-bold'}>Certificaten:</span> Aantal certificaten dat binnen deze periode is uitgegeven.</li>
        </ul>
      </> : <>
        <ul>
          <li>&middot; <span className={'font-bold'}>Modules Toegewezen:</span> Opsomming van het totaal aantal modules dat toegewezen is aan medewerkers binnen het team in deze periode.</li>
          <li>&middot; <span className={'font-bold'}>Modules Gestart:</span> Aantal modules waar medewerkers binnen deze periode aan zijn begonnen.</li>
          <li>&middot; <span className={'font-bold'}>Modules Afgerond:</span> Aantal modules welke medewerkers binnen deze periode hebben afgerond.</li>
          <li>&middot; <span className={'font-bold'}>Modules Vervallen:</span> Opsomming van het aantal modules dat binnen deze periode vervallen is. Dit gebeurt wanneer de medewerker een module nog niet afgerond heeft, en uit dienst gaat.</li>
          <li>&middot; <span className={'font-bold'}>Certificaten:</span> Aantal certificaten dat binnen deze periode is uitgegeven.</li>
        </ul>
      </>}

      <div className={'my-4 flex items-center justify-start'}>
        <div className={'flex flex-row space-x-4 items-center justify-center bg-white border border-gray-200 py-2 px-4 rounded-lg w-72'}>
          <div>Modules</div>
          <Toggle value={showModuleTable} onChange={setShowModuleTable} label={''} />
          <div>Medewerkers</div>
        </div>
      </div>


      <div className={"mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white"}>
        <table className={"w-full border-collapse"}>
          <thead>
          <tr className={"bg-gray-50 text-left"}>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              Team
            </th>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              {showModuleTable ? 'Medewerkers toegewezen' : 'Modules toegewezen'}
            </th>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              {showModuleTable ? 'Medewerkers gestart' : 'Modules gestart'}
            </th>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              {showModuleTable ? 'Medewerkers afgerond' : 'Modules afgerond'}
            </th>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              {showModuleTable ? 'Medewerkers uitgetreden' : 'Modules vervallen'}
            </th>
            <th className={"px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"}>
              Certificaten uitgedeeld
            </th>
            <th className={"px-4 py-3"}></th>
          </tr>
          </thead>
          <tbody>
          {parentTeams.map((pt, j) => {
            return <TeamProgressTableRow key={j} team={pt} teams={explorerTeams}  />
          })}
          </tbody>
        </table>
      </div>

    </div>
  </>
}

const TeamProgressTableRow: FC<{ team: LearnDashboardExplorerTeam, teams: LearnDashboardExplorerTeam[], indentLevel?: number }> = ({team, teams, indentLevel = 0}) => {
  const {learn} = useApiCall(config)
  const navigate = useNavigate()
  const {fromDate, toDate, selectedModuleIds, selectedCategories, showModuleTable} = useLearnDashboardExplorerContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const [statistics, setStatistics] = useState<LearnDashboardStatistic|null>(null)
  const subTeamsMap = useMemo(() => {
    return teams.filter((t) => t.parentObjectId === team.id && t.hasMyTeamAsChild)
  }, [teams])

  const teamIds = useMemo(() => {
    return [team.id, ...getSubTeamsRecursively(teams, team.id).map(t => t.id)]
  }, [team, teams])
  const fetchStatistics = async (from: Date, upTo: Date, modules: string[], teams: number[]) => {
    await learn.fetchTeamProgressDashboardStatistics(from, upTo, modules, teams).then((result) => {
      setStatistics(result)
    })
  }

  useEffect(() => {
    if (selectedModuleIds.length > 0 && !isExpanded && team.isMyTeam) {
      fetchStatistics(fromDate, toDate, selectedModuleIds, teamIds)
    }
  }, [selectedModuleIds, teamIds, fromDate, toDate, isExpanded, team]);

  return <>
  <tr className={`border-t border-gray-200 h-14 ${subTeamsMap.length > 0 ? 'hover:bg-red-50 hover:cursor-pointer' : ''}`} onClick={() => {
    if (subTeamsMap.length <= 0) {
      return {}
    } else {
      return setIsExpanded(prev => !prev)
    }
  }}>
    <td className={"pr-4 text-sm text-gray-500"} style={{paddingLeft: `calc(1rem + ${indentLevel*15}px)`}}>
      <div className={'flex space-x-3 items-center'}>
        <div className={'h-5 w-5 flex items-center justify-center'}>
          {subTeamsMap.length === 0 ? <>
            <FontAwesomeIcon icon={faDot} className={'text-3xl text-brand-primary'} />
          </> : <>
            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className={`text-xl text-brand-primary`}/>
          </>}
        </div>
        <div className={"text-brand-on-primary-container font-semibold"}>{team.name}</div>
      </div>
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      {!isExpanded && <div className={'flex flex-row space-x-1'}>
        <div>{showModuleTable ? statistics?.employees_assigned.this_period ?? 0 : statistics?.assigned_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={showModuleTable ? statistics.employees_assigned.this_period : statistics.assigned_modules.this_period}
                                                previous={showModuleTable ? statistics.employees_assigned.previous_period : statistics.assigned_modules.previous_period}/>
          : <div></div>}
      </div>}
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      {!isExpanded && <div className={'flex flex-row space-x-1'}>
        <div>{showModuleTable ? statistics?.employees_started.this_period ?? 0 : statistics?.started_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={showModuleTable ? statistics?.employees_started.this_period ?? 0 : statistics.started_modules.this_period}
                                                previous={showModuleTable ? statistics?.employees_started.previous_period ?? 0 : statistics.started_modules.previous_period}/>
          : <div></div>}
      </div>}
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      {!isExpanded && <div className={'flex flex-row space-x-1'}>
        <div>{showModuleTable ? statistics?.employees_finished.this_period ?? 0 : statistics?.finished_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={showModuleTable ? statistics?.employees_finished.this_period ?? 0 : statistics.finished_modules.this_period}
                                                previous={showModuleTable ? statistics?.employees_finished.previous_period ?? 0 : statistics.finished_modules.previous_period}/>
          : <div></div>}
      </div>}
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      {!isExpanded && <div className={'flex flex-row space-x-1'}>
        <div>{showModuleTable ? statistics?.employees_stopped.this_period ?? 0 : statistics?.dropped_modules.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={showModuleTable ? statistics?.employees_stopped.this_period ?? 0 : statistics.dropped_modules.this_period}
                                                previous={showModuleTable ? statistics?.employees_stopped.previous_period ?? 0 : statistics.dropped_modules.previous_period}/>
          : <div></div>}
      </div>}
    </td>

    <td className={"px-4 py-3 text-sm text-gray-500"}>
      {!isExpanded && <div className={'flex flex-row space-x-1'}>
        <div>{statistics?.awarded_certificates.this_period ?? 0}</div>
        {statistics ? <StatisticChangeIndicator current={statistics.awarded_certificates.this_period}
                                                previous={statistics.awarded_certificates.previous_period}/>
          : <div></div>}
      </div>}
    </td>

    <td className={"px-4 py-3 w-80"}>
      {subTeamsMap.length === 0 && team.isMyTeam && <div className={"flex flex-row items-center space-x-2 text-sm text-brand-primary transition hover:cursor-pointer hover:text-red-500"}
        onClick={() => {
          navigate(`/dashboards-learn/team?from=${moment(fromDate).format('DD-MM-yyyy')}&to=${moment(toDate).format('DD-MM-yyyy')}&team=${team.id}&categories=${selectedCategories.join(',')}`)
        }}>
        <div>Team Dashboard</div>
        <FontAwesomeIcon icon={faChevronRight}/>
      </div>}
    </td>
  </tr>

  {isExpanded && subTeamsMap.map((st, k) => {
    return <TeamProgressTableRow key={k} team={st} teams={teams} indentLevel={indentLevel + 1} />
  })}
  </>
}


const StatisticChangeIndicator: FC<{ current: number, previous: number}> = (props) => {
  const style = props.current > props.previous ? 'text-green-600'
    : props.current === props.previous ? 'text-amber-600'
      : 'text-red-600'

  return <div className={`${style}`}>
    ({props.current > props.previous && '+'}{Math.round((props.current - props.previous)/Math.max(1,props.previous) * 100) }%)
  </div>
}
